import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut, Loader2, Car, CalendarCheck, FileText, Plus, CalendarClock, User, Phone, Download, Lock,
  LayoutDashboard, UserCog, Menu, X, MapPin, Save, Mail, ChevronRight, ImagePlus, Trash2, Check,
  Wrench, Zap, Gauge, Cog, ShieldCheck, Palette, CalendarPlus,
} from "lucide-react";
import { toast } from "sonner";
import { useClientAuth } from "@/context/ClientAuthContext";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { CarteGriseScanner } from "@/components/CarteGriseScanner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { clientApi, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { AppointmentChat } from "@/components/AppointmentChat";
import { PhoneField } from "@/components/PhoneField";
import { useLang } from "@/context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND_URL}${u}`);
const inputCls = "bg-[#050505] border-white/10 rounded-none h-12 text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0";

const ACCENTS = {
  cyan: { hex: "#00E5FF", label: "Cyan Électrique" },
  red: { hex: "#E60000", label: "Rouge Spelt" },
  violet: { hex: "#A855F7", label: "Violet Néon" },
  emerald: { hex: "#10D9A0", label: "Vert Menthe" },
  amber: { hex: "#F5A524", label: "Ambre" },
  magenta: { hex: "#FF3D8B", label: "Magenta" },
};
const AVATAR_PRESETS = { wrench: Wrench, car: Car, bolt: Zap, gauge: Gauge, cog: Cog, shield: ShieldCheck };
const accHex = (u) => ACCENTS[u?.accent]?.hex || ACCENTS.cyan.hex;

const BANNERS = ["aurora", "grid", "glow", "carbon", "sunset", "mono"];
function bannerStyle(user, acc) {
  if (user?.banner_image) return { backgroundImage: `linear-gradient(90deg, rgba(5,5,7,0.88), rgba(5,5,7,0.35)), url(${resolveUrl(user.banner_image)})`, backgroundSize: "cover", backgroundPosition: "center" };
  switch (user?.banner || "aurora") {
    case "grid": return { backgroundColor: "#0A0A0C", backgroundImage: `linear-gradient(90deg,#050505,transparent), linear-gradient(rgba(255,255,255,0.05) 1px,transparent 1px), linear-gradient(90deg,rgba(255,255,255,0.05) 1px,transparent 1px)`, backgroundSize: "auto, 26px 26px, 26px 26px" };
    case "glow": return { background: `radial-gradient(120% 140% at 85% 15%, ${acc}45, transparent 55%), #0A0A0C` };
    case "carbon": return { backgroundColor: "#0A0A0C", backgroundImage: `repeating-linear-gradient(45deg, rgba(255,255,255,0.045) 0 2px, transparent 2px 9px)` };
    case "sunset": return { background: `linear-gradient(100deg,#1a0b0b, ${acc}33 55%, #2a1206)` };
    case "mono": return { background: "#0A0A0C", borderBottom: `2px solid ${acc}` };
    default: return { background: `linear-gradient(110deg,#050505 18%, ${acc}30 60%, ${acc}55)` };
  }
}

function Banner({ user }) {
  const { t } = useLang();
  const acc = accHex(user);
  return (
    <div className="relative overflow-hidden border border-white/10 mb-8 h-[150px] sm:h-[168px] flex items-end" style={bannerStyle(user, acc)} data-testid="dash-banner">
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
      <div className="relative p-6 sm:p-8 flex items-center gap-4">
        <Avatar user={user} className="w-14 h-14" iconCls="w-6 h-6" />
        <div>
          <h2 className="font-display font-900 text-white text-2xl sm:text-3xl tracking-tight uppercase leading-none">{user?.name?.split(" ")[0] || t("portal.account")}</h2>
          <p className="font-mono-tech text-[11px] uppercase tracking-[0.15em] mt-1.5" style={{ color: acc }}>{t("portal.banner_welcome_sub")}</p>
        </div>
      </div>
    </div>
  );
}

const STATUS_CLS = {
  pending: "text-amber-400 border-amber-400/40 bg-amber-400/10",
  quoted: "text-fuchsia-400 border-fuchsia-400/40 bg-fuchsia-400/10",
  confirmed: "text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/10",
  completed: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10",
  cancelled: "text-zinc-400 border-zinc-500/40 bg-zinc-500/10",
  declined: "text-[#FF1A1A] border-[#FF1A1A]/40 bg-[#FF1A1A]/10",
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return iso; } };

const vKey = (v) => {
  if (!v) return "__none__";
  const plate = (v.immatriculation || "").trim().toUpperCase();
  if (plate) return plate;
  const mm = `${v.marque || ""} ${v.modele || ""}`.trim().toUpperCase();
  return mm || "__none__";
};

const groupByVehicle = (appts, saved = []) => {
  const map = new Map();
  for (const a of appts) {
    const k = vKey(a.vehicle);
    if (!map.has(k)) map.set(k, { key: k, vehicle: a.vehicle || null, appts: [] });
    const g = map.get(k);
    g.appts.push(a);
    if (a.vehicle) {
      const filled = Object.fromEntries(Object.entries(a.vehicle).filter(([, val]) => val != null && String(val).trim() !== ""));
      g.vehicle = { ...(g.vehicle || {}), ...filled };
    }
  }
  for (const v of saved) {
    const k = vKey(v);
    if (map.has(k)) { map.get(k).vid = v.id; map.get(k).vehicle = { ...v, ...(map.get(k).vehicle || {}) }; }
    else { map.set(k, { key: k, vehicle: v, appts: [], vid: v.id }); }
  }
  return Array.from(map.values()).sort((x, y) => (x.key === "__none__" ? 1 : 0) - (y.key === "__none__" ? 1 : 0));
};

function Avatar({ user, className = "w-11 h-11", iconCls = "w-5 h-5" }) {
  const acc = accHex(user);
  const Preset = user?.avatar ? AVATAR_PRESETS[user.avatar] : null;
  if (Preset) return <div className={`${className} flex items-center justify-center border rounded-full bg-white/5 shrink-0`} style={{ borderColor: acc, color: acc }}><Preset className={iconCls} /></div>;
  if (user?.picture) return <img src={resolveUrl(user.picture)} alt="" className={`${className} rounded-full object-cover border shrink-0`} style={{ borderColor: acc }} />;
  const initials = (user?.name || user?.email || "?").trim()[0]?.toUpperCase() || "?";
  return <div className={`${className} flex items-center justify-center border rounded-full bg-white/5 font-display font-900 shrink-0`} style={{ borderColor: acc, color: acc }}>{initials}</div>;
}

// -- Initial profile completion wall (name + phone + structured address) ---
function ProfileGate({ user, onDone, onLogout }) {
  const { t } = useLang();
  const [f, setF] = useState({ name: user?.name || "", phone: user?.phone || "", addr_street: user?.addr_street || user?.address || "", addr_number: user?.addr_number || "", addr_zip: user?.addr_zip || "", addr_city: user?.addr_city || "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.phone.trim() || !f.addr_street.trim() || !f.addr_number.trim() || !f.addr_zip.trim() || !f.addr_city.trim()) { toast.error(t("portal.profile_required")); return; }
    setSaving(true);
    try {
      const { data } = await clientApi.patch("/client/profile", f);
      onDone(data);
      toast.success(t("portal.profile_saved"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const lbl = "font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2";
  return (
    <div className="noise-overlay bg-[#050505] min-h-screen flex items-center justify-center px-5 py-10 grid-bg" data-testid="profile-gate">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo height="h-11" /></div>
        <div className="flex justify-center items-center gap-3 mb-8"><LanguageSwitcher /><ThemeToggle /></div>
        <div className="border border-white/10 bg-[#0C0C0E] p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center border border-[#00E5FF]/40 bg-[#00E5FF]/10"><User className="w-4 h-4 text-[#00E5FF]" /></div>
            <h1 className="font-display font-900 text-white text-xl tracking-tight uppercase">{t("portal.profile_title")}</h1>
          </div>
          <p className="text-zinc-500 text-sm mb-8">{t("portal.profile_sub")}</p>
          <form onSubmit={save} className="space-y-5">
            <div><label className={lbl}>{t("portal.profile_name")}</label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" placeholder="" className={inputCls} /></div>
            <div><label className={lbl}>{t("portal.profile_phone")}</label><PhoneField value={f.phone} onChange={(v) => set("phone", v)} testId="profile-phone" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><label className={lbl}>{t("portal.addr_street")}</label><Input value={f.addr_street} onChange={(e) => set("addr_street", e.target.value)} data-testid="profile-address" placeholder={t("portal.addr_street_ph")} className={inputCls} /></div>
              <div><label className={lbl}>{t("portal.addr_number")}</label><Input value={f.addr_number} onChange={(e) => set("addr_number", e.target.value)} data-testid="profile-addr-number" placeholder={t("portal.addr_number_ph")} className={inputCls} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={lbl}>{t("portal.addr_zip")}</label><Input value={f.addr_zip} onChange={(e) => set("addr_zip", e.target.value)} data-testid="profile-addr-zip" placeholder={t("portal.addr_zip_ph")} className={inputCls} /></div>
              <div className="col-span-2"><label className={lbl}>{t("portal.addr_city")}</label><Input value={f.addr_city} onChange={(e) => set("addr_city", e.target.value)} data-testid="profile-addr-city" placeholder={t("portal.addr_city_ph")} className={inputCls} /></div>
            </div>
            <button type="submit" disabled={saving} data-testid="profile-save" className="w-full inline-flex items-center justify-center gap-2 h-12 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Phone className="w-4 h-4" /> {t("portal.profile_save")}</>}
            </button>
          </form>
          <button onClick={onLogout} data-testid="profile-logout" className="mt-5 w-full text-center font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-600 hover:text-white transition-colors">{t("portal.logout")}</button>
        </div>
      </div>
    </div>
  );
}

function ApptCard({ a, onSend, isNew }) {
  const { t, tf } = useLang();
  return (
    <div className={`border bg-[#0C0C0E] ${isNew ? "border-[#E60000]/50" : "border-white/10"}`} data-testid={`client-appt-${a.id}`}>
      <div className="p-5 sm:p-6 grid md:grid-cols-2 gap-5">
        <div>
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <span className="font-display font-700 text-white text-lg flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-[#E60000]" /> {tf(`services.${a.service_id}.label`, a.service_label)}</span>
            <span className={`px-2 py-0.5 text-[10px] font-mono-tech uppercase tracking-wider border ${STATUS_CLS[a.status] || STATUS_CLS.pending}`} data-testid={`client-status-${a.id}`}>{t(`status.${a.status}`)}</span>
            {isNew && <span className="px-2 py-0.5 text-[10px] font-mono-tech uppercase tracking-wider bg-[#E60000] text-white animate-pulse" data-testid={`client-new-${a.id}`}>{t("portal.new_badge")}</span>}
          </div>
          <div className="font-mono-tech text-sm text-zinc-400">{a.preferred_date} · {a.preferred_time}</div>
          {a.vehicle?.marque && <div className="flex items-center gap-2 text-zinc-500 text-sm mt-2"><Car className="w-3.5 h-3.5" /> {a.vehicle.marque} {a.vehicle.modele} {a.vehicle.immatriculation ? `· ${a.vehicle.immatriculation}` : ""}</div>}
          {a.message && <p className="text-zinc-600 text-xs mt-3 italic max-w-md">“{a.message}”</p>}
          <p className="text-zinc-600 text-[11px] font-mono-tech mt-3">{t("portal.requested_on")} {fmtDate(a.created_at)}</p>
        </div>
        <div className="md:border-l md:border-white/10 md:pl-5">
          {a.quote?.total != null ? (
            <div className="border border-fuchsia-400/30 bg-fuchsia-400/5 p-4">
              <div className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-fuchsia-400" /><span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-fuchsia-300">{t("portal.quote")}</span></div>
              <div className="text-2xl font-display font-900 text-white">{Number(a.quote.total).toFixed(2)} €</div>
              <span className="text-zinc-500 text-xs">{t("portal.quote_total")}</span>
              {a.quote_token && (
                <Link to={`/devis/${a.quote_token}`} data-testid={`client-view-quote-${a.id}`} className="mt-3 block text-center px-4 py-2.5 bg-fuchsia-500 text-white font-display font-700 uppercase tracking-wide text-xs hover:opacity-90 transition-opacity">
                  {t("portal.view_quote")}
                </Link>
              )}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-zinc-600 text-xs font-mono-tech text-center py-4">{t("portal.no_quote")}</div>
          )}
        </div>
      </div>
      <div className="border-t border-white/10 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4"><span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-[#00E5FF]">{t("portal.chat_title")}</span></div>
        <AppointmentChat messages={a.chat_messages || []} onSend={(text) => onSend(a.id, text)} viewerRole="client" testidPrefix={`client-chat-${a.id}`} />
      </div>
    </div>
  );
}

function Overview({ user, groups, appts, onBook }) {
  const { t } = useLang();
  const acc = accHex(user);
  const vehicleCount = groups.filter((g) => g.key !== "__none__").length;
  const quotes = appts.filter((a) => a.status === "quoted").length;
  const upcoming = appts
    .filter((a) => ["pending", "confirmed", "quoted"].includes(a.status) && a.preferred_date)
    .sort((x, y) => `${x.preferred_date}${x.preferred_time}`.localeCompare(`${y.preferred_date}${y.preferred_time}`));
  const next = upcoming[0];
  const cards = [
    { key: "vehicles", label: t("portal.ov_vehicles"), value: vehicleCount, icon: Car, accent: true },
    { key: "requests", label: t("portal.ov_requests"), value: appts.length, icon: CalendarCheck, color: "text-white", bar: "bg-white" },
    { key: "upcoming", label: t("portal.ov_upcoming"), value: upcoming.length, icon: CalendarClock, color: "text-amber-400", bar: "bg-amber-400" },
    { key: "quotes", label: t("portal.ov_quotes"), value: quotes, icon: FileText, color: "text-fuchsia-400", bar: "bg-fuchsia-400" },
  ];
  return (
    <div className="space-y-8">
      <Banner user={user} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="dash-stats">
        {cards.map((c, i) => (
          <motion.div key={c.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.06 }} className="relative bg-[#0C0C0E] p-6 overflow-hidden group" data-testid={`dash-stat-${c.key}`}>
            <span className={`absolute left-0 top-0 h-full w-[3px] opacity-60 group-hover:opacity-100 transition-opacity ${c.accent ? "" : c.bar}`} style={c.accent ? { backgroundColor: acc } : undefined} />
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500">{c.label}</span>
              <c.icon className={`w-4 h-4 ${c.color || ""}`} style={c.accent ? { color: acc } : undefined} />
            </div>
            <span className={`font-display font-900 text-4xl tracking-tighter ${c.color || ""}`} style={c.accent ? { color: acc } : undefined}>{c.value}</span>
          </motion.div>
        ))}
      </div>
      <div className="border p-6 sm:p-8" style={{ borderColor: `${acc}40`, boxShadow: `0 0 25px ${acc}12` }} data-testid="dash-next-appt">
        <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em]" style={{ color: acc }}>{t("portal.ov_next_title")}</span>
        {next ? (
          <div className="mt-4 flex items-center justify-between gap-5 flex-wrap">
            <div>
              <div className="font-display font-900 text-white text-2xl sm:text-3xl tracking-tight uppercase">{next.preferred_date}</div>
              <div className="font-mono-tech mt-1" style={{ color: acc }}>{next.preferred_time} · {t(`status.${next.status}`)}</div>
              {next.vehicle?.marque && <div className="flex items-center gap-2 text-zinc-500 text-sm mt-2"><Car className="w-3.5 h-3.5" /> {next.vehicle.marque} {next.vehicle.modele}</div>}
            </div>
            <button onClick={onBook} className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 text-white font-display font-700 uppercase tracking-wide text-sm hover:border-[#E60000] hover:text-[#E60000] transition-colors"><Plus className="w-4 h-4" /> {t("portal.book_cta")}</button>
          </div>
        ) : (
          <div className="mt-4 flex items-center justify-between gap-5 flex-wrap">
            <p className="text-zinc-500 text-sm max-w-md">{t("portal.ov_next_none")}</p>
            <button onClick={onBook} data-testid="dash-overview-book" className="inline-flex items-center gap-2 px-5 py-3 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide text-sm border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors"><Plus className="w-4 h-4" /> {t("portal.book_cta")}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileEdit({ user, onSaved }) {
  const { t } = useLang();
  const acc = accHex(user);
  const [f, setF] = useState({
    name: user?.name || "", phone: user?.phone || "",
    addr_street: user?.addr_street || (user?.addr_city ? "" : user?.address) || "", addr_number: user?.addr_number || "",
    addr_zip: user?.addr_zip || "", addr_city: user?.addr_city || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const bannerRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const lbl = "font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2";

  const savePrefs = async (patch) => {
    try { const { data } = await clientApi.patch("/client/preferences", patch); onSaved(data); toast.success(t("portal.prefs_saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("JPEG / PNG / WEBP"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await clientApi.post("/client/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } }); onSaved(data.user); toast.success(t("portal.prefs_saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const uploadBanner = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("JPEG / PNG / WEBP"); return; }
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await clientApi.post("/client/banner", fd, { headers: { "Content-Type": "multipart/form-data" } }); onSaved(data.user); toast.success(t("portal.prefs_saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { if (bannerRef.current) bannerRef.current.value = ""; }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.phone.trim() || !f.addr_street.trim() || !f.addr_number.trim() || !f.addr_zip.trim() || !f.addr_city.trim()) { toast.error(t("portal.profile_required")); return; }
    setSaving(true);
    try { const { data } = await clientApi.patch("/client/profile", f); onSaved(data); toast.success(t("portal.profile_saved")); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-zinc-500 text-sm">{t("portal.prof_edit_sub")}</p>

      {/* Personnalisation */}
      <div className="border border-white/10 bg-[#0C0C0E] p-6 sm:p-8" data-testid="profile-perso">
        <div className="flex items-center gap-2 mb-6"><Palette className="w-4 h-4" style={{ color: acc }} /><span className="font-mono-tech text-[11px] uppercase tracking-[0.15em]" style={{ color: acc }}>{t("portal.prof_perso")}</span></div>
        <div className="flex items-center gap-5 mb-6 flex-wrap">
          <Avatar user={user} className="w-20 h-20" iconCls="w-8 h-8" />
          <div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="avatar-file" onChange={(e) => uploadAvatar(e.target.files?.[0])} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="avatar-upload" className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/20 text-white font-mono-tech text-[11px] uppercase tracking-[0.14em] hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors disabled:opacity-50">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {uploading ? t("portal.prof_uploading") : t("portal.prof_upload")}
            </button>
          </div>
        </div>
        <label className={lbl}>{t("portal.prof_presets")}</label>
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(AVATAR_PRESETS).map(([k, Icon]) => (
            <button key={k} onClick={() => savePrefs({ avatar: k })} data-testid={`avatar-preset-${k}`} className={`w-12 h-12 flex items-center justify-center border rounded-full transition-colors ${user?.avatar === k ? "bg-white/10" : "border-white/15 text-zinc-400 hover:text-white hover:border-white/40"}`} style={user?.avatar === k ? { borderColor: acc, color: acc } : undefined}>
              <Icon className="w-5 h-5" />
            </button>
          ))}
        </div>
        <label className={lbl}>{t("portal.prof_accent")}</label>
        <div className="flex flex-wrap gap-3">
          {Object.entries(ACCENTS).map(([k, v]) => (
            <button key={k} onClick={() => savePrefs({ accent: k })} title={v.label} data-testid={`accent-${k}`} className="w-9 h-9 rounded-full flex items-center justify-center transition-transform hover:scale-110" style={{ backgroundColor: v.hex, boxShadow: user?.accent === k ? `0 0 0 2px #0C0C0E, 0 0 0 4px ${v.hex}` : "none" }}>
              {user?.accent === k && <Check className="w-4 h-4 text-black" />}
            </button>
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-white/10">
          <label className={lbl}>{t("portal.prof_banner")}</label>
          <p className="text-zinc-600 text-xs mb-4 -mt-1">{t("portal.prof_banner_sub")}</p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
            {BANNERS.map((b) => {
              const activeB = !user?.banner_image && (user?.banner || "aurora") === b;
              return (
                <button key={b} onClick={() => savePrefs({ banner: b })} data-testid={`banner-${b}`} title={t(`portal.banner_${b}`)}
                  className={`h-14 border overflow-hidden relative transition-transform hover:scale-[1.03] ${activeB ? "" : "border-white/12"}`}
                  style={{ ...bannerStyle({ banner: b }, acc), ...(activeB ? { borderColor: acc, boxShadow: `0 0 0 1px ${acc}` } : {}) }}>
                  {activeB && <Check className="w-4 h-4 absolute top-1 right-1 text-white drop-shadow" />}
                  <span className="absolute bottom-1 left-1.5 font-mono-tech text-[8px] uppercase tracking-wider text-white/80">{t(`portal.banner_${b}`)}</span>
                </button>
              );
            })}
          </div>
          <input ref={bannerRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="banner-file" onChange={(e) => uploadBanner(e.target.files?.[0])} />
          <button onClick={() => bannerRef.current?.click()} data-testid="banner-upload" className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/20 text-white font-mono-tech text-[11px] uppercase tracking-[0.14em] hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors">
            <ImagePlus className="w-4 h-4" /> {t("portal.banner_upload")}
          </button>
        </div>
      </div>

      {/* Coordonnées + adresse */}
      <form onSubmit={saveProfile} className="border border-white/10 bg-[#0C0C0E] p-6 sm:p-8 space-y-5" data-testid="profile-edit-form">
        <div className="flex items-center gap-2 mb-1"><UserCog className="w-4 h-4 text-[#E60000]" /><span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-300">{t("portal.prof_contact")}</span></div>
        <div>
          <label className={lbl}>{t("portal.email")}</label>
          <div className="flex items-center gap-2 h-12 px-3 border border-white/10 bg-[#050505] text-zinc-500 font-mono-tech text-sm"><Mail className="w-4 h-4 text-zinc-600" /> {user?.email}</div>
          <p className="text-zinc-700 text-[10px] font-mono-tech mt-1.5">{t("portal.prof_email_locked")}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={lbl}>{t("portal.profile_name")}</label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-edit-name" className={inputCls} /></div>
          <div><label className={lbl}>{t("portal.profile_phone")}</label><PhoneField value={f.phone} onChange={(v) => set("phone", v)} testId="profile-edit-phone" /></div>
        </div>
        <div className="flex items-center gap-2 pt-2"><MapPin className="w-4 h-4 text-[#E60000]" /><span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-300">{t("portal.prof_address")}</span></div>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2"><label className={lbl}>{t("portal.addr_street")}</label><Input value={f.addr_street} onChange={(e) => set("addr_street", e.target.value)} data-testid="profile-edit-address" placeholder={t("portal.addr_street_ph")} className={inputCls} /></div>
          <div><label className={lbl}>{t("portal.addr_number")}</label><Input value={f.addr_number} onChange={(e) => set("addr_number", e.target.value)} data-testid="profile-edit-number" placeholder={t("portal.addr_number_ph")} className={inputCls} /></div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div><label className={lbl}>{t("portal.addr_zip")}</label><Input value={f.addr_zip} onChange={(e) => set("addr_zip", e.target.value)} data-testid="profile-edit-zip" placeholder={t("portal.addr_zip_ph")} className={inputCls} /></div>
          <div className="col-span-2"><label className={lbl}>{t("portal.addr_city")}</label><Input value={f.addr_city} onChange={(e) => set("addr_city", e.target.value)} data-testid="profile-edit-city" placeholder={t("portal.addr_city_ph")} className={inputCls} /></div>
        </div>
        <button type="submit" disabled={saving} data-testid="profile-edit-save" className="inline-flex items-center justify-center gap-2 h-12 px-7 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> {t("portal.prof_save")}</>}
        </button>
      </form>
    </div>
  );
}

function AddVehicleModal({ open, onOpenChange, onAdded }) {
  const { t } = useLang();
  const [veh, setVeh] = useState({});
  const [saving, setSaving] = useState(false);
  const setK = (k, v) => setVeh((p) => ({ ...p, [k]: v }));
  const fields = [
    { k: "marque", label: t("booking.v_marque"), ph: t("booking.v_marque_ph") },
    { k: "modele", label: t("booking.v_modele"), ph: t("booking.v_modele_ph") },
    { k: "annee", label: t("booking.v_annee"), ph: t("booking.v_annee_ph") },
    { k: "immatriculation", label: t("booking.v_immat"), ph: t("booking.v_immat_ph") },
    { k: "vin", label: t("booking.v_vin"), ph: t("booking.v_vin_ph") },
    { k: "cylindree", label: t("booking.v_cylindree"), ph: t("booking.v_cylindree_ph") },
  ];
  const save = async () => {
    const clean = Object.fromEntries(Object.entries(veh).filter(([, v]) => v != null && String(v).trim() !== ""));
    if (!clean.marque && !clean.immatriculation) { toast.error(t("portal.vehicle_required")); return; }
    setSaving(true);
    try { const { data } = await clientApi.post("/client/vehicles", clean); onAdded(data); toast.success(t("portal.vehicle_added")); onOpenChange(false); setVeh({}); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0C0C0E] border border-white/10 text-white rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display font-900 uppercase tracking-tight flex items-center gap-2"><Car className="w-5 h-5 text-[#E60000]" /> {t("portal.add_vehicle_title")}</DialogTitle></DialogHeader>
        <p className="text-zinc-500 text-sm">{t("portal.add_vehicle_sub")}</p>
        <div className="my-2"><CarteGriseScanner onDetected={(v) => setVeh((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(v || {}).filter(([, val]) => val != null && String(val).trim() !== "")) }))} /></div>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="add-vehicle-fields">
          {fields.map((fd) => (
            <div key={fd.k}>
              <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{fd.label}</label>
              <Input value={veh[fd.k] || ""} onChange={(e) => setK(fd.k, e.target.value)} data-testid={`add-veh-${fd.k}`} placeholder={fd.ph} className={inputCls} />
            </div>
          ))}
        </div>
        <button onClick={save} disabled={saving} data-testid="add-vehicle-save" className="mt-3 w-full inline-flex items-center justify-center gap-2 h-12 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> {t("portal.add_vehicle_save")}</>}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function GarageView({ user, groups, appts, dlKey, onDownload, onSend, onAdd, onBookVehicle, onDeleteVehicle }) {
  const { t } = useLang();
  const acc = accHex(user);
  return (
    <div className="space-y-6" data-testid="client-garage">
      <div className="flex justify-end">
        <button onClick={onAdd} data-testid="garage-add-vehicle" className="inline-flex items-center gap-2 px-5 py-3 border font-display font-700 uppercase tracking-wide text-sm transition-colors" style={{ borderColor: acc, color: acc }}>
          <Plus className="w-4 h-4" /> {t("portal.add_vehicle")}
        </button>
      </div>
      {groups.length === 0 ? (
        <div className="border border-white/10 bg-[#0C0C0E] py-20 text-center" data-testid="client-empty">
          <Car className="w-8 h-8 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-500 font-mono-tech text-sm mb-6">{t("portal.empty")}</p>
          <button onClick={onAdd} className="inline-flex items-center gap-2 px-6 py-3 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide text-sm hover:opacity-90 transition-opacity"><Plus className="w-4 h-4" /> {t("portal.add_vehicle")}</button>
        </div>
      ) : groups.map((g) => (
        <div key={g.key} data-testid={`garage-vehicle-${g.key}`}>
          <div className="border p-5 sm:p-6 mb-4" style={g.key === "__none__" ? { borderColor: "rgba(255,255,255,0.12)" } : { borderColor: `${acc}40`, boxShadow: `0 0 20px ${acc}0d` }}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center border shrink-0" style={g.key === "__none__" ? { borderColor: "rgba(255,255,255,0.15)" } : { borderColor: `${acc}66`, backgroundColor: `${acc}1a` }}><Car className="w-6 h-6" style={{ color: g.key === "__none__" ? "#a1a1aa" : acc }} /></div>
                <div>
                  <h3 className="font-display font-900 text-white text-xl uppercase tracking-tight leading-none">{g.vehicle?.marque ? `${g.vehicle.marque} ${g.vehicle.modele || ""}` : t("portal.no_vehicle")}</h3>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap font-mono-tech text-[11px] text-zinc-500">
                    {g.vehicle?.annee && <span>{g.vehicle.annee}</span>}
                    {g.vehicle?.energie && <span>· {g.vehicle.energie}</span>}
                    {g.vehicle?.cylindree && <span>· {g.vehicle.cylindree}</span>}
                    {g.vehicle?.vin && <span className="truncate max-w-[180px]">· VIN {g.vehicle.vin}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {g.vehicle?.immatriculation && <span className="px-3 py-1.5 border border-white/25 bg-white/5 font-mono-tech font-700 text-[#E60000] tracking-[0.12em]" data-testid={`garage-plate-${g.key}`}>{g.vehicle.immatriculation}</span>}
                {g.key !== "__none__" && (
                  <button onClick={() => onBookVehicle(g.vehicle)} data-testid={`garage-book-${g.key}`} className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E60000] text-white font-mono-tech text-[10px] uppercase tracking-[0.12em] border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors"><CalendarPlus className="w-3.5 h-3.5" /> {t("portal.book_this")}</button>
                )}
                {g.key !== "__none__" && (
                  g.appts.some((a) => a.status === "completed") ? (
                    <button onClick={() => onDownload(g)} disabled={dlKey === g.key} data-testid={`garage-booklet-${g.key}`} className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00E5FF]/40 bg-[#00E5FF]/10 text-[#00E5FF] font-mono-tech text-[10px] uppercase tracking-[0.12em] hover:bg-[#00E5FF] hover:text-black transition-colors disabled:opacity-50">
                      {dlKey === g.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {dlKey === g.key ? t("portal.downloading") : t("portal.download_booklet")}
                    </button>
                  ) : (
                    <span data-testid={`garage-booklet-locked-${g.key}`} title={t("portal.booklet_locked")} className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 text-zinc-500 font-mono-tech text-[10px] uppercase tracking-[0.12em]"><Lock className="w-3.5 h-3.5" /> {t("portal.booklet_locked")}</span>
                  )
                )}
                {g.vid && g.appts.length === 0 && (
                  <button onClick={() => onDeleteVehicle(g.vid)} data-testid={`garage-delete-${g.key}`} title={t("portal.vehicle_delete_confirm")} className="w-8 h-8 flex items-center justify-center border border-white/10 text-zinc-500 hover:border-[#E60000] hover:text-[#E60000] transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            </div>
          </div>
          {g.appts.length > 0 && (
            <div className="space-y-4 sm:pl-5 sm:border-l" style={{ borderColor: `${acc}26` }}>
              {g.appts.map((a) => <ApptCard key={a.id} a={a} onSend={onSend} />)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const NAV = [
  { key: "overview", labelKey: "portal.dash_overview", icon: LayoutDashboard },
  { key: "garage", labelKey: "portal.dash_garage", icon: Car },
  { key: "appointments", labelKey: "portal.dash_appointments", icon: CalendarCheck },
  { key: "profile", labelKey: "portal.dash_profile", icon: UserCog },
];

function SidebarContent({ user, active, setActive, onLogout, onBook, closeMobile, unread = 0 }) {
  const { t } = useLang();
  const acc = accHex(user);
  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-6 pb-5 border-b border-white/10"><Logo height="h-9" /></div>
      <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
        <Avatar user={user} />
        <div className="min-w-0">
          <div className="font-display font-700 text-white text-sm truncate" data-testid="client-name-label">{user?.name || t("portal.account")}</div>
          <div className="font-mono-tech text-[11px] text-zinc-500 truncate">{user?.email}</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-5 space-y-1">
        {NAV.map((n) => {
          const isActive = active === n.key;
          const showBadge = n.key === "appointments" && unread > 0 && !isActive;
          return (
            <button key={n.key} onClick={() => { setActive(n.key); closeMobile?.(); }} data-testid={`dash-nav-${n.key}`}
              className={`relative w-full flex items-center gap-3 px-4 py-3 font-mono-tech text-[12px] uppercase tracking-[0.14em] transition-colors ${isActive ? "" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
              style={isActive ? { color: acc, backgroundColor: `${acc}1a` } : undefined}>
              {isActive && <motion.span layoutId="nav-bar" className="absolute left-0 top-0 h-full w-[3px]" style={{ backgroundColor: acc, boxShadow: `0 0 10px ${acc}99` }} />}
              <n.icon className="w-4 h-4 shrink-0" /> {t(n.labelKey)}
              {showBadge && <span className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-[#E60000] text-white font-display font-900 text-[10px]" data-testid="nav-unread-badge">{unread}</span>}
              {isActive && <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
            </button>
          );
        })}
      </nav>
      <div className="px-4 pb-4 space-y-3">
        <button onClick={onBook} data-testid="client-book-cta" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide text-sm border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors"><Plus className="w-4 h-4" /> {t("portal.book_cta")}</button>
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2"><LanguageSwitcher /><ThemeToggle className="w-9 h-9" /></div>
          <button onClick={onLogout} data-testid="client-logout" title={t("portal.logout")} className="w-9 h-9 flex items-center justify-center border border-white/15 text-zinc-400 hover:border-[#E60000] hover:text-[#E60000] transition-colors"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

export default function ClientDashboard() {
  const { user, loading: authLoading, logout, updateUser } = useClientAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dlKey, setDlKey] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const loggingOut = useRef(false);

  useEffect(() => { if (!authLoading && !user && !loggingOut.current) navigate("/login", { replace: true }); }, [authLoading, user, navigate]);

  const load = useCallback(async (silent) => {
    try { const { data } = await clientApi.get("/client/appointments"); setAppts(data); }
    catch (e) { if (!silent) toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);
  useEffect(() => { if (!user) return; const id = setInterval(() => load(true), 12000); return () => clearInterval(id); }, [user, load]);

  const groups = useMemo(() => groupByVehicle(appts, user?.vehicles || []), [appts, user]);

  const seenKey = user ? `gts_seen_${user.email}` : "gts_seen";
  const sig = (a) => `${(a.chat_messages || []).filter((m) => m.sender === "admin").length}|${a.quote?.total ?? ""}`;
  const hasGarageActivity = (a) => (a.chat_messages || []).some((m) => m.sender === "admin") || a.quote?.total != null;
  const [seen, setSeen] = useState({});
  const [newAtOpen, setNewAtOpen] = useState(() => new Set());
  useEffect(() => { try { setSeen(JSON.parse(localStorage.getItem(seenKey) || "{}")); } catch { setSeen({}); } }, [seenKey]);
  const unreadIds = useMemo(() => appts.filter((a) => hasGarageActivity(a) && sig(a) !== seen[a.id]).map((a) => a.id), [appts, seen]);
  useEffect(() => {
    if (active === "appointments" && !loading && appts.length) {
      setNewAtOpen(new Set(appts.filter((a) => hasGarageActivity(a) && sig(a) !== seen[a.id]).map((a) => a.id)));
      const next = {}; appts.forEach((a) => { next[a.id] = sig(a); });
      setSeen(next); localStorage.setItem(seenKey, JSON.stringify(next));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, loading]);

  const sendMessage = async (apptId, text) => {
    try { const { data } = await clientApi.post(`/appointments/${apptId}/messages`, { text }); setAppts((prev) => prev.map((a) => a.id === apptId ? { ...a, chat_messages: [...(a.chat_messages || []), data] } : a)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doLogout = () => { loggingOut.current = true; navigate("/"); logout(); };
  const goBook = () => navigate("/booking");
  const bookVehicle = (vehicle) => navigate("/booking", { state: { vehicle } });
  const onVehiclesChanged = (vehicles) => updateUser({ ...user, vehicles });
  const deleteVehicle = async (vid) => {
    try { const { data } = await clientApi.delete(`/client/vehicles/${vid}`); onVehiclesChanged(data); toast.success(t("portal.vehicle_removed")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadBooklet = async (g) => {
    setDlKey(g.key);
    try {
      const { data } = await clientApi.get(`/client/vehicle-booklet?v=${encodeURIComponent(g.key)}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `carnet-${(g.vehicle?.immatriculation || g.vehicle?.marque || "vehicule")}.pdf`.replace(/\s/g, "-");
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(t("portal.download_error")); }
    finally { setDlKey(null); }
  };

  if (authLoading || !user) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-600 font-mono-tech text-sm">…</div>;
  if (!user.profile_complete) return <ProfileGate user={user} onDone={updateUser} onLogout={doLogout} />;

  const acc = accHex(user);
  const sectionTitle = t(NAV.find((n) => n.key === active)?.labelKey || "portal.dash_overview");

  return (
    <div className="noise-overlay bg-[#050505] min-h-screen" data-testid="client-dashboard">
      <aside className="hidden lg:flex flex-col fixed left-0 top-0 h-screen w-[260px] bg-[#0A0A0C] border-r border-white/10 z-40">
        <SidebarContent user={user} active={active} setActive={setActive} onLogout={doLogout} onBook={goBook} unread={unreadIds.length} />
      </aside>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobileOpen(false)} className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-40" data-testid="dash-drawer-backdrop" />
            <motion.aside initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }} transition={{ type: "tween", duration: 0.25 }} className="lg:hidden fixed left-0 top-0 h-screen w-[260px] bg-[#0A0A0C] border-r border-white/10 z-50">
              <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-3 w-9 h-9 flex items-center justify-center text-zinc-400 hover:text-white z-10" data-testid="dash-drawer-close"><X className="w-5 h-5" /></button>
              <SidebarContent user={user} active={active} setActive={setActive} onLogout={doLogout} onBook={goBook} closeMobile={() => setMobileOpen(false)} unread={unreadIds.length} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="lg:ml-[260px] min-h-screen flex flex-col">
        <header className="sticky top-0 z-30 border-b border-white/10 bg-black/70 backdrop-blur-xl">
          <div className="h-[64px] px-5 sm:px-8 flex items-center gap-4">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden w-10 h-10 flex items-center justify-center border border-white/15 text-white hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors" data-testid="dash-mobile-menu"><Menu className="w-5 h-5" /></button>
            <div>
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em]" style={{ color: acc }}>{t("portal.ov_hello")}, {user?.name?.split(" ")[0] || ""}</p>
              <h1 className="font-display font-900 text-white text-lg sm:text-xl tracking-tight uppercase leading-none mt-0.5" data-testid="dash-section-title">{sectionTitle}</h1>
            </div>
          </div>
        </header>

        <main className="flex-1 px-5 sm:px-8 py-8 max-w-5xl w-full">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                {active === "overview" && <Overview user={user} groups={groups} appts={appts} onBook={goBook} />}
                {active === "garage" && <GarageView user={user} groups={groups} appts={appts} dlKey={dlKey} onDownload={downloadBooklet} onSend={sendMessage} onAdd={() => setAddOpen(true)} onBookVehicle={bookVehicle} onDeleteVehicle={deleteVehicle} />}
                {active === "appointments" && (
                  appts.length === 0 ? (
                    <div className="border border-white/10 bg-[#0C0C0E] py-20 text-center" data-testid="client-empty">
                      <CalendarClock className="w-8 h-8 text-zinc-600 mx-auto mb-4" />
                      <p className="text-zinc-500 font-mono-tech text-sm mb-6">{t("portal.empty")}</p>
                      <button onClick={goBook} className="inline-flex items-center gap-2 px-6 py-3 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide text-sm hover:opacity-90 transition-opacity"><Plus className="w-4 h-4" /> {t("portal.book_cta")}</button>
                    </div>
                  ) : (
                    <div className="space-y-4" data-testid="client-appointments-list">
                      <p className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 mb-2">{appts.length} · {t("portal.appts_all")}</p>
                      {appts.map((a) => <ApptCard key={a.id} a={a} onSend={sendMessage} isNew={newAtOpen.has(a.id)} />)}
                    </div>
                  )
                )}
                {active === "profile" && <ProfileEdit user={user} onSaved={updateUser} />}
              </motion.div>
            </AnimatePresence>
          )}
        </main>
      </div>

      <AddVehicleModal open={addOpen} onOpenChange={setAddOpen} onAdded={onVehiclesChanged} />
    </div>
  );
}
